import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/auth/AuthProvider';
import { TrainingCalendar } from './TrainingCalendar';
import { Activity, Target, TrendingUp, Heart, Timer, Zap, Calendar, Flame } from 'lucide-react';

interface EnhancedOverviewProps {
  timeRange: string;
}

export const EnhancedOverview: React.FC<EnhancedOverviewProps> = ({ timeRange }) => {
  const { user } = useAuth();

  const { data: overviewData, isLoading } = useQuery({
    queryKey: ['enhanced-overview', timeRange],
    queryFn: async () => {
      if (!user) return null;

      const dateLimit = getDateLimit(timeRange);
      let query = supabase
        .from('running_performance')
        .select('*')
        .order('recorded_date', { ascending: false });

      if (dateLimit) {
        query = query.gte('recorded_date', dateLimit);
      }

      const { data: runs, error } = await query.limit(100);
      if (error) throw error;

      // Calculate insights
      const totalRuns = runs?.length || 0;
      const totalDistance = runs?.reduce((sum, run) => sum + (run.distance_meters || 0), 0) || 0;
      const totalTime = runs?.reduce((sum, run) => sum + (run.time_seconds || 0), 0) || 0;

      // Training consistency (last 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const recentRuns = runs?.filter(run => 
        new Date(run.recorded_date) >= thirtyDaysAgo
      ) || [];
      
      const uniqueRunDays = new Set(recentRuns.map(run => run.recorded_date)).size;
      const consistencyScore = Math.round((uniqueRunDays / 30) * 100);

      // Weekly distance trend
      const currentWeek = getWeekRuns(runs || [], 0);
      const lastWeek = getWeekRuns(runs || [], 1);
      const currentWeekDistance = currentWeek.reduce((sum, run) => sum + (run.distance_meters || 0), 0) / 1000;
      const lastWeekDistance = lastWeek.reduce((sum, run) => sum + (run.distance_meters || 0), 0) / 1000;
      const weeklyTrend = currentWeekDistance - lastWeekDistance;

      // Recent performance trend
      const recentPaces = runs?.slice(0, 5).map(r => r.pace_per_km_seconds).filter(Boolean) || [];
      const olderPaces = runs?.slice(5, 10).map(r => r.pace_per_km_seconds).filter(Boolean) || [];
      const recentAvgPace = recentPaces.length ? recentPaces.reduce((a, b) => a + b, 0) / recentPaces.length : 0;
      const olderAvgPace = olderPaces.length ? olderPaces.reduce((a, b) => a + b, 0) / olderPaces.length : 0;
      const paceImprovement = olderAvgPace - recentAvgPace; // negative means slower

      // Training distribution
      const easyRuns = runs?.filter(r => r.run_type === 'easy').length || 0;
      const tempoRuns = runs?.filter(r => r.run_type === 'tempo').length || 0;
      const intervalRuns = runs?.filter(r => r.run_type === 'interval').length || 0;
      const longRuns = runs?.filter(r => r.run_type === 'long').length || 0;

      // Check for recent PR
      const { data: recentPR } = await supabase
        .from('running_personal_records')
        .select('*')
        .order('achieved_date', { ascending: false })
        .limit(1)
        .single();

      return {
        totalRuns,
        totalDistance: Math.round(totalDistance / 1000 * 10) / 10,
        totalTime: Math.round(totalTime / 3600 * 10) / 10,
        consistencyScore,
        weeklyTrend,
        paceImprovement,
        currentWeekDistance: Math.round(currentWeekDistance * 10) / 10,
        trainingDistribution: { easyRuns, tempoRuns, intervalRuns, longRuns },
        recentPR: recentPR && new Date(recentPR.achieved_date) > thirtyDaysAgo ? recentPR : null,
        runs: runs || []
      };
    },
    enabled: !!user,
  });

  const getDateLimit = (range: string) => {
    const now = new Date();
    switch (range) {
      case '1m': return new Date(now.setMonth(now.getMonth() - 1)).toISOString().split('T')[0];
      case '3m': return new Date(now.setMonth(now.getMonth() - 3)).toISOString().split('T')[0];
      case '6m': return new Date(now.setMonth(now.getMonth() - 6)).toISOString().split('T')[0];
      case '1y': return new Date(now.setFullYear(now.getFullYear() - 1)).toISOString().split('T')[0];
      default: return null;
    }
  };

  const getWeekRuns = (runs: any[], weeksAgo: number) => {
    const now = new Date();
    const weekStart = new Date(now);
    weekStart.setDate(now.getDate() - now.getDay() - (weeksAgo * 7));
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);

    return runs.filter(run => {
      const runDate = new Date(run.recorded_date);
      return runDate >= weekStart && runDate <= weekEnd;
    });
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-32 bg-muted rounded-lg animate-pulse" />
        <div className="h-96 bg-muted rounded-lg animate-pulse" />
      </div>
    );
  }

  if (!overviewData?.runs.length) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="p-8 text-center">
            <Activity className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-semibold mb-2">No Running Data</h3>
            <p className="text-muted-foreground">Start logging your runs to see detailed analytics</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Quick Insights */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <Flame className="h-8 w-8 mx-auto mb-2 text-orange-600" />
            <div className="text-2xl font-bold">{overviewData.consistencyScore}%</div>
            <div className="text-sm text-muted-foreground">Consistency</div>
            <Badge variant={overviewData.consistencyScore >= 70 ? 'default' : 'secondary'} className="mt-1 text-xs">
              {overviewData.consistencyScore >= 70 ? 'Great!' : 'Keep Going'}
            </Badge>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 text-center">
            <Target className="h-8 w-8 mx-auto mb-2 text-blue-600" />
            <div className="text-2xl font-bold">{overviewData.currentWeekDistance}km</div>
            <div className="text-sm text-muted-foreground">This Week</div>
            <div className={`text-xs mt-1 ${
              overviewData.weeklyTrend > 0 ? 'text-green-600' : 
              overviewData.weeklyTrend < 0 ? 'text-red-600' : 'text-gray-600'
            }`}>
              {overviewData.weeklyTrend > 0 ? '+' : ''}{overviewData.weeklyTrend.toFixed(1)}km vs last week
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 text-center">
            <TrendingUp className="h-8 w-8 mx-auto mb-2 text-purple-600" />
            <div className={`text-2xl font-bold ${
              overviewData.paceImprovement > 0 ? 'text-green-600' : 
              overviewData.paceImprovement < 0 ? 'text-red-600' : 'text-gray-600'
            }`}>
              {overviewData.paceImprovement > 0 ? '↗' : overviewData.paceImprovement < 0 ? '↘' : '→'}
            </div>
            <div className="text-sm text-muted-foreground">Pace Trend</div>
            <div className="text-xs text-muted-foreground mt-1">
              {Math.abs(overviewData.paceImprovement)} sec/km
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 text-center">
            <Zap className="h-8 w-8 mx-auto mb-2 text-yellow-600" />
            {overviewData.recentPR ? (
              <>
                <div className="text-xl font-bold">PR!</div>
                <div className="text-sm text-muted-foreground">Recent PB</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {(overviewData.recentPR.distance_meters / 1000)}K
                </div>
              </>
            ) : (
              <>
                <div className="text-xl font-bold">-</div>
                <div className="text-sm text-muted-foreground">No Recent PR</div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Training Distribution */}
      <Card>
        <CardHeader>
          <CardTitle>Training Distribution</CardTitle>
          <CardDescription>Breakdown of run types in selected period</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <Badge variant="secondary" className="mb-2">Easy</Badge>
              <div className="text-2xl font-bold text-green-600">
                {overviewData.trainingDistribution.easyRuns}
              </div>
              <div className="text-sm text-muted-foreground">
                {Math.round((overviewData.trainingDistribution.easyRuns / overviewData.totalRuns) * 100)}%
              </div>
            </div>
            <div className="text-center">
              <Badge variant="secondary" className="mb-2">Tempo</Badge>
              <div className="text-2xl font-bold text-orange-600">
                {overviewData.trainingDistribution.tempoRuns}
              </div>
              <div className="text-sm text-muted-foreground">
                {Math.round((overviewData.trainingDistribution.tempoRuns / overviewData.totalRuns) * 100)}%
              </div>
            </div>
            <div className="text-center">
              <Badge variant="secondary" className="mb-2">Intervals</Badge>
              <div className="text-2xl font-bold text-red-600">
                {overviewData.trainingDistribution.intervalRuns}
              </div>
              <div className="text-sm text-muted-foreground">
                {Math.round((overviewData.trainingDistribution.intervalRuns / overviewData.totalRuns) * 100)}%
              </div>
            </div>
            <div className="text-center">
              <Badge variant="secondary" className="mb-2">Long</Badge>
              <div className="text-2xl font-bold text-blue-600">
                {overviewData.trainingDistribution.longRuns}
              </div>
              <div className="text-sm text-muted-foreground">
                {Math.round((overviewData.trainingDistribution.longRuns / overviewData.totalRuns) * 100)}%
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Training Calendar */}
      <TrainingCalendar timeRange={timeRange} />
    </div>
  );
};